<template>
	<div
		class="textarea "
		:class="{
			'is-disabled': disabled,
			'is-required': required,
			'has-error': hasError
		}"
	>
		<Label
			:for="id"
			class="textarea__label"
			v-if="label"
			:labelText="label"
		/>
		<div class="textarea__input-container">
			<textarea
				class="textarea__input"
				ref="textarea"
				:id="id"
				:name="name"
				:rows="rows"
				:cols="cols"
				:placeholder="placeholder"
				:class="className"
				:value="value"
				:maxLength="maxLength"
				:required="required"
				@input="updateValue"
				@blur="handleBlur"
			/>
			<div class="textarea__icon-container" v-if="hasError">
				<span class="textarea__icon textarea__icon--error">
					<i class="icon icon-error-solid"></i>
				</span>
			</div>
		</div>
		<div class="textarea__text-container">
			<div class="textarea__text-left">
				<div
					class="textarea__text textarea__text--error"
					v-if="hasError"
				>
					{{ error }}
				</div>
				<HelpFeedback
					:helpText="helpText"
					v-if="helpText"
				/>
			</div>
			<div class="textarea__text-right">
				<div
					class="textarea__text"
					v-show="
						hasCharactersRemaining || showAlwaysCharacterCounter
					"
				>
					{{ totalCharacters }} / {{ maxLength }}
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import Label from '@/components/forms/label/label';
import { required } from 'vuelidate/lib/validators';
import HelpFeedback from '@/components/forms/help-feedback/help-feedback';
import _ from 'lodash';

export default {
	name: 'Textarea',
	components: {
		Label,
		HelpFeedback
	},
	props: {
		label: {
			type: String,
			default: ''
		},
		labelErrorMessage: {
			type: String,
			default: ''
		},
		className: {
			type: String,
			default: ''
		},
		placeholder: {
			type: String,
			default: ''
		},
		rows: {
			type: Number,
			default: 0
		},
		cols: {
			type: Number,
			default: 0
		},
		maxLength: {
			type: Number,
			default: 0
		},
		helpText: {
			type: String,
			default: ''
		},
		required: {
			type: Boolean,
			default: false
		},
		value: {
			type: String,
			default: ''
		},
		showAlwaysCharacterCounter: {
			type: Boolean,
			default: false
		},
		forceCustomErrorMessage: {
			type: String,
			default: ''
		},
		showErrorMessage: {
			type: Boolean,
			default: true
		}
	},
	computed: {
		error() {
			if (this.showErrorMessage) {
				if (this.forceCustomErrorMessage)
					return this.forceCustomErrorMessage;

				if (_.get(this.$v, 'value.$anyError', false)) {
					if (!this.$v.value.required) {
						return `${this.labelErrorMessage ||
							this.label ||
							'Field'} is required`;
					}
				}
			}

			return '';
		},
		hasError() {
			return !!this.error;
		},
		hasCharactersRemaining() {
			return this.totalCharacters < this.maxLength;
		},
		totalCharacters() {
			return this.value ? this.value.length : 0;
		}
	},
	validations() {
		let result = {};
		if (this.required) {
			result.value = {
				required
			};
		}
		return { ...result };
	},
	methods: {
		updateValue(e) {
			this.$emit('input', e.target.value);
			this.handleBlur();
		},
		handleBlur() {
			if (this.$v && this.$v.value) {
				return this.$v.value.$touch();
			}
		}
	},
	watch: {
		hasError: {
			immediate: true,
			handler(val) {
				this.$emit('hasError', {
					[this.name]: val
				});
			}
		}
	}
};
</script>

<style lang="scss" scoped>
/**
    * Textarea styles
    * Can use SCSS variables = TRUE
*/

@import '@/scss/5-objects/_form.scss';

.textarea {
	position: relative;

	&__input {
		@include textfield;
		cursor: pointer;
		font-size: $input-font-size;
		text-overflow: ellipsis;
		padding: $input-padding-y $input-padding-x;
		width: 100%;

		&:focus {
			-o-outline: $focus-outline;
			-moz-outline: $focus-outline;
			outline: $focus-outline;
			outline-offset: $focus-offset;
		}

		overflow-y: auto;
		@include scrollbar('small');
	}

	&__input-container {
		position: relative;
	}

	&__icon-container {
		position: absolute;
		right: $input-padding-x;
		top: $input-padding-y;
	}

	&__icon {
		padding-left: $gutter-sm;
		font-size: $input-font-size + 2;
	}

	&__icon--error {
		display: none;
	}

	&__text-container {
		display: flex;
		justify-content: space-between;
	}

	&__text-right {
		margin-left: auto;
		text-align: right;
		padding-left: $padding-md;
	}
}

/*------------------------------------*\
	#TEXTAREA CHARACTER COUNT
\*------------------------------------*/

 .textarea__text {
 	font-size: $label-font-size - 2;
 	margin-top: $gutter-sm;
	color: $neutral-2
 }

/*------------------------------------*\
  #ERROR
\*------------------------------------*/

.has-error {
	.textarea__input {
		padding-right: 28px;
		border-color: $ui-negative;
	}

	.textarea__icon--error {
		display: contents;
		color: $ui-negative;
	}

	.textarea__text--error {
		display: block;
		color: $ui-negative;
	}
}
</style>
